<template>
  <div>
    <fieldset
      v-for="group in categoryGroups"
      :key="group.name"
      v-memo="[
        group.name,
        categoryIds,
        selectedSubcategoryFacetSlug,
        showCategoryCount,
        categoryFacetCounts,
        isElectricFacetEnabled,
      ]"
      :class="{ desktop: $isDesktop }"
    >
      <legend>{{ group.name }}</legend>
      <template
        v-for="(category, index) in group.categories"
        :key="category.id"
      >
        <span :class="{ 'is-byo-main-category': category.isByo }">
          <base-checkbox
            :name="category.slug"
            :model-value="categoryIds.includes(category.id)"
            :class="{
              'category-checkbox': true,
              disabled: !isEnabled(category),
            }"
            @update:model-value="onToggle(category, $event)"
          >
            <span class="category-icon-container">
              <pu-category-icons
                :categories="[{ ...category, isPositive: true }]"
                :data-test="`category-selector-${category.slug}`"
                class="category-icon"
              />
              <transition name="fade">
                <span
                  v-if="category.isByo"
                  :class="{
                    count: true,
                    'count-hidden': !(
                      showCategoryCount && selectedSubcategoryFacetSlug === null
                    ),
                  }"
                >
                  {{ getCategoryCount(category) }}
                </span>
              </transition>
            </span>
            <template v-if="!category.isByo">
              {{ category.pretty_name }}
            </template>
            <transition name="fade">
              <span
                v-if="!category.isByo"
                :class="{
                  count: true,
                  'count-hidden': !(
                    showCategoryCount && selectedSubcategoryFacetSlug === null
                  ),
                }"
              >
                {{ getCategoryCount(category) }}
              </span>
            </transition>

            <!-- Use the slot to hide the visual for BYO categories -->
            <template v-if="category.isByo" #checkboxVisual><span /></template>
          </base-checkbox>

          <div
            v-if="
              category.isByo &&
              category.subcategories &&
              category.subcategories.length > 0
            "
            class="subcategories-wrapper"
          >
            <base-checkbox
              v-for="subcategory in category.subcategories"
              :key="subcategory.id"
              :name="`subcat-${subcategory.id}`"
              :model-value="isSubcategoryChecked(subcategory, category)"
              :class="{
                'subcategory-checkbox': true,
                'is-empty': isSubcategoryDisabledAndEmpty(subcategory),
                'parent-selected-state': isParentOnlySelected(
                  subcategory,
                  category,
                ),
              }"
              @update:model-value="onToggleSubcategory(subcategory, $event)"
            >
              <span>{{ subcategory.name }}</span>
              <transition name="fade">
                <span
                  :class="{
                    count: true,
                    'subcategory-count': true,
                    'count-hidden': !(
                      showCategoryCount &&
                      categoryIds.length <= 1 &&
                      (selectedSubcategoryFacetSlug === null ||
                        selectedSubcategoryFacetSlug === subcategory.facetSlug)
                    ),
                  }"
                >
                  {{ getSubcategoryCount(subcategory) }}
                </span>
              </transition>
            </base-checkbox>
          </div>
        </span>
        <div
          v-if="category.isByo && index === group.categories.length - 1"
          class="is-byo-main-category electric-facet-toggle"
        >
          <base-checkbox
            :slide-toggle="true"
            :model-value="isElectricFacetEnabled"
            @update:model-value="onToggleElectric"
          >
            <img
              src="@/assets/images/electric.svg"
              width="18"
              height="23"
              loading="lazy"
              alt=""
            />
            <translate>Electric pitch</translate>
          </base-checkbox>
        </div>
      </template>
    </fieldset>
    <nuxt-to-meta :nuxt2meta="head" />
  </div>
</template>

<script lang="ts">
import type { FacetCount } from '~/apps/facets/domain/FacetCounts'
import {
  Component,
  Prop,
  toNative,
  Vue,
  Watch,
} from '~/utility/pu-class-decorator'
import { CampsiteCategory } from '~/utility/types/CampsiteCategory'
import { getCampsiteCategoriesPresenter } from '../presenter-json/CampsiteCategoriesUpdaterJson'
import { getCategoryFacetLinker } from '../services/CategoryFacetLinker'

type AnySubcategoryType = CampsiteCategory['subcategories'][0]

@Component({
  emits: [
    'changeCategoryIds',
    'subcategoryGroupChange',
    'subcategoryChange',
    'electricFacetChange',
  ],
  setup() {
    const pinia = usePinia()
    const searchWizardStore = useSearchWizardStore(pinia)
    const facetsStore = useFacetsStore(pinia)
    return {
      searchWizardStore,
      facetsStore,
    }
  },
})
class CategoriesSelector extends Vue {
  facetsStore: ReturnType<typeof useFacetsStore>
  selectedSubcategoryFacetSlug: string | null = null
  private categoryFacetLinker = getCategoryFacetLinker()
  private isUpdatingSubcategory = false

  @Prop({ default: false, type: Boolean })
  isSearchForm: boolean

  @Prop({ required: false })
  categoryFacetCounts?: FacetCount

  @Prop({ required: true, type: Array })
  categoryIds: string[]

  @Prop({ required: false })
  allowedCategoryIds?: string[]

  @Prop({ required: false })
  isReactiveCounts?: boolean

  @Prop({ default: () => [], type: Array as () => string[] })
  facets: string[]

  localElectricEnabled: boolean | null = null

  @Watch('facets', { immediate: true, deep: true })
  onFacetsChange() {
    this.initializeSelectedSubcategory()
    this.localElectricEnabled = null
  }

  get isElectricFacetEnabled() {
    return this.localElectricEnabled ?? this.facets.includes('electric')
  }

  onToggleElectric(checked: boolean) {
    this.localElectricEnabled = checked
    this.$emit('electricFacetChange', checked)
  }

  mounted() {
    // Component is ready for user interactions
  }

  /**
   * Validates user selections and emits corrections if needed
   * Only called during actual user interactions
   */
  private validateUserSelection(categoryIds: string[], facets: string[]) {
    // Skip validation during subcategory updates to prevent race conditions
    if (this.isUpdatingSubcategory) {
      return
    }

    // Skip validation when parent component doesn't handle subcategoryGroupChange events
    const hasSubcategoryListener = 'onSubcategoryGroupChange' in this.$attrs
    if (!hasSubcategoryListener && this.selectedSubcategoryFacetSlug) {
      return
    }

    const validation = this.categoryFacetLinker.validateAndCorrect(
      categoryIds,
      facets,
    )

    if (!validation.isValid) {
      // If validation found issues, emit the corrected values
      if (
        JSON.stringify(validation.correctedCategoryIds) !==
        JSON.stringify(categoryIds)
      ) {
        this.$emit('changeCategoryIds', validation.correctedCategoryIds)
      }

      // Update selected subcategory facet if it was removed
      if (
        this.selectedSubcategoryFacetSlug &&
        !validation.correctedFacets.includes(this.selectedSubcategoryFacetSlug)
      ) {
        this.selectedSubcategoryFacetSlug = null
        this.emitSubcategoryChange()
      }
    }

    // Also handle the specific case of subcategory parent validation
    if (this.selectedSubcategoryFacetSlug) {
      const parent = this.getParentCategoryOfSubcategory(
        this.selectedSubcategoryFacetSlug,
      )
      let shouldClearSubcategory = false
      if (parent) {
        if (!categoryIds.includes(parent.id)) {
          // Parent category was removed, clear the subcategory
          shouldClearSubcategory = true
        } else if (parent.isByo && categoryIds.length !== 1) {
          // For BYO categories, maintain single selection requirement
          shouldClearSubcategory = true
        }
        // For non-BYO categories, allow multiple selections
      } else {
        shouldClearSubcategory = true
      }

      if (
        shouldClearSubcategory &&
        this.selectedSubcategoryFacetSlug !== null
      ) {
        this.selectedSubcategoryFacetSlug = null
        this.emitSubcategoryChange()
      }
    }
  }

  get head() {
    return {
      bodyAttrs: {
        class: this.isSearchForm && !this.$isDesktop ? 'noscroll' : '',
      },
    }
  }

  get showCategoryCount() {
    return !!(
      this.categoryFacetCounts &&
      Object.keys(this.categoryFacetCounts).length > 0
    )
  }

  get categoryGroups() {
    const lang = (this.$route.params.lang as string) || 'en-gb'
    const allCategories = getCampsiteCategoriesPresenter(lang).list

    // --- BYO Categories --- //
    const byoCategoriesRaw = allCategories.filter((c) => c.isByo)
    // Guessed rootSlugs and desired order for BYO main categories
    const byoOrder = ['tents', 'campervans', 'touring-caravans']
    const sortedByoCategoriesMapped = byoCategoriesRaw
      .sort((a, b) => {
        const indexA = byoOrder.indexOf(a.rootSlug)
        const indexB = byoOrder.indexOf(b.rootSlug)

        // If both items are in the defined order, sort by their index
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB
        }
        // If only 'a' is in the defined order, 'a' comes first
        if (indexA !== -1) {
          return -1
        }
        // If only 'b' is in the defined order, 'b' comes first
        if (indexB !== -1) {
          return 1 // Corrected spacing for linter
        }

        // Otherwise (neither is in the defined order), sort alphabetically
        const nameA = a.pretty_name || a.name || ''
        const nameB = b.pretty_name || b.name || ''
        return nameA.localeCompare(nameB)
      })
      .map(this.mapCategory)

    // --- Onsite Categories (existing logic from previous turn) --- //
    const onsiteCategoriesRaw = allCategories.filter((c) => !c.isByo)
    const onsiteOrder = ['lodges', 'rent-a-tent', 'caravans']
    const sortedOnsiteCategoriesMapped = onsiteCategoriesRaw
      .sort((a, b) => {
        const indexA = onsiteOrder.indexOf(a.rootSlug)
        const indexB = onsiteOrder.indexOf(b.rootSlug)
        if (indexA !== -1 && indexB !== -1) return indexA - indexB
        if (indexA !== -1) return -1
        if (indexB !== -1) return 1
        const nameA = a.pretty_name || a.name || ''
        const nameB = b.pretty_name || b.name || ''
        return nameA.localeCompare(nameB)
      })
      .map(this.mapCategory)

    return [
      {
        name: this.$t('Bring my own'),
        categories: sortedByoCategoriesMapped,
      },
      {
        name: this.$t('Onsite accommodation'),
        categories: sortedOnsiteCategoriesMapped,
      },
    ]
  }

  mapCategory(category: CampsiteCategory) {
    let prettyName = category.pretty_name
    if (category.rootSlug === 'lodges') {
      prettyName = this.$t("Pod, shepherd's hut, cabin, lodge and more")
    } else if (category.rootSlug === 'rent-a-tent') {
      prettyName = this.$t('Bell tent, safari tent, yurt, ready tent and more')
    } else if (category.rootSlug === 'caravans') {
      prettyName = this.$t('Static caravan, onsite touring caravan')
    }

    let subcategoriesToReturn = category.subcategories.filter(
      (s) => s.facetSlug && this.facetsStore.facetsBySlug(s.facetSlug),
    )

    // Custom sort for subcategories of the main 'tents' BYO category
    if (category.rootSlug === 'tents') {
      const tentSubOrder = ['Tent', 'Roof tent'] // Order by subcategory.name
      subcategoriesToReturn = subcategoriesToReturn.slice().sort((a, b) => {
        const indexA = tentSubOrder.indexOf(a.name)
        const indexB = tentSubOrder.indexOf(b.name)

        if (indexA !== -1 && indexB !== -1) return indexA - indexB
        if (indexA !== -1) return -1
        if (indexB !== -1) return 1
        return a.name.localeCompare(b.name) // Fallback to alphabetical for others
      })
    } else {
      // Default sort for other categories' subcategories
      subcategoriesToReturn = subcategoriesToReturn.slice().sort((a, b) => {
        return a.name.localeCompare(b.name)
      })
    }

    return {
      ...category,
      pretty_name: prettyName,
      subcategories: subcategoriesToReturn,
    }
  }

  onToggle(category: CampsiteCategory, checked: boolean) {
    if (checked) {
      this.selectCategory(category)
    } else {
      this.unselectCategory(category)
    }
  }

  selectCategory(category: CampsiteCategory) {
    let newCategoryIds: string[]

    // For BYO categories, maintain single selection behavior
    if (category.isByo) {
      if (this.selectedSubcategoryFacetSlug !== null) {
        // A subcategory IS currently selected.
        const parentOfActiveSubcategory = this.getParentCategoryOfSubcategory(
          this.selectedSubcategoryFacetSlug,
        )

        if (
          parentOfActiveSubcategory &&
          parentOfActiveSubcategory.id === category.id
        ) {
          // Case 1: The clicked category IS the parent of the currently active subcategory.
          // Action: Do nothing to subcategory, ensure categoryIds is just [parent.id].
          newCategoryIds = [category.id]
        } else {
          // Case 2: The clicked category is NOT the parent of the currently active subcategory.
          // Action: Deactivate the current subcategory, and make the clicked category the ONLY selected one.
          this.selectedSubcategoryFacetSlug = null
          this.emitSubcategoryChange()
          newCategoryIds = [category.id]
        }
      } else {
        // NO subcategory is currently selected. For BYO, still enforce single selection.
        // Clear other BYO categories when selecting a new one
        newCategoryIds = this.categoryIds.filter((id) => {
          const cat = this.getCategoryById(id)
          return cat && !cat.isByo
        })
        newCategoryIds.push(category.id)
      }
    } else {
      // For non-BYO categories, always allow multi-selection
      if (this.categoryIds.includes(category.id)) {
        // If it's already selected, no change to the list of IDs.
        newCategoryIds = this.categoryIds.slice()
      } else {
        // Add the non-BYO category to existing selection
        newCategoryIds = this.categoryIds.slice()
        newCategoryIds.push(category.id)

        // If a BYO subcategory was selected and we're adding a non-BYO category,
        // clear the subcategory selection and remove the BYO parent category
        if (this.selectedSubcategoryFacetSlug !== null) {
          const parentOfActiveSubcategory = this.getParentCategoryOfSubcategory(
            this.selectedSubcategoryFacetSlug,
          )
          if (parentOfActiveSubcategory && parentOfActiveSubcategory.isByo) {
            // Remove the BYO parent category from selection
            newCategoryIds = newCategoryIds.filter(
              (id) => id !== parentOfActiveSubcategory.id,
            )
            this.selectedSubcategoryFacetSlug = null
            this.emitSubcategoryChange()
          }
        }
      }
    }
    this.submitCategories(newCategoryIds)
  }

  unselectCategory(category: CampsiteCategory) {
    // Use CategoryFacetLinker to remove category and its dependent facets
    const result = this.categoryFacetLinker.removeCategoryWithFacets(
      this.categoryIds,
      this.facets,
      category.id,
    )

    // Update selectedSubcategoryFacetSlug if any of the removed facets was selected
    if (this.selectedSubcategoryFacetSlug !== null) {
      const availableFacets =
        this.categoryFacetLinker.getAvailableFacetsForCategory(category.id)
      if (availableFacets.includes(this.selectedSubcategoryFacetSlug)) {
        this.selectedSubcategoryFacetSlug = null
        this.emitSubcategoryChange()
      }
    }

    this.submitCategories(result.categoryIds)
  }

  submitCategories(categoryIds: string[]) {
    // Validate user's category selection before emitting
    this.validateUserSelection(categoryIds, this.facets)
    this.$emit('changeCategoryIds', categoryIds)
  }

  getCategoryCount(category: CampsiteCategory): number {
    const countIfNone = 0
    return this.categoryFacetCounts
      ? this.categoryFacetCounts[category.slug] || countIfNone
      : countIfNone
  }

  getSubcategoryCount(subcategory: AnySubcategoryType) {
    const countIfNone = ''
    return this.categoryFacetCounts
      ? this.categoryFacetCounts[subcategory.facetSlug] || countIfNone
      : countIfNone
  }

  isEnabled(category: CampsiteCategory) {
    if (!this.categoryIds) {
      return true
    }
    if (this.categoryIds.includes(category.id)) {
      return true
    }
    const isAllowed = this.isAllowed(category.id)
    const isAllowedByCount = this.isAllowedByCount(category)
    return isAllowed && isAllowedByCount
  }

  isAllowedByCount(category: CampsiteCategory): boolean {
    if (!this.categoryFacetCounts || !this.showCategoryCount) {
      return true
    }
    return this.getCategoryCount(category) > 0
  }

  isAllowed(categoryId: string): boolean {
    if (!this.allowedCategoryIds) {
      return true
    }
    return this.allowedCategoryIds.includes(categoryId)
  }

  isSubcategoryDisabledAndEmpty(subcategory: AnySubcategoryType): boolean {
    const count = this.getSubcategoryCount(subcategory)
    return (
      this.showCategoryCount &&
      (count === 0 || count === '') &&
      this.categoryIds.length === 0 &&
      !this.selectedSubcategoryFacetSlug
    )
  }

  /**
   * Returns true if the given subcategory is the globally selected one
   * or if its parent is selected without any specific subcategory
   */
  isSubcategoryChecked(
    subcategory: AnySubcategoryType,
    category: CampsiteCategory,
  ) {
    // Specific subcategory is selected
    if (this.selectedSubcategoryFacetSlug === subcategory.facetSlug) {
      return true
    }

    // Parent is selected but no specific subcategory is selected
    const parentOnlySelected = this.isParentOnlySelected(subcategory, category)
    return parentOnlySelected
  }

  /**
   * Returns true if the parent category is selected but no specific subcategory
   * is selected, indicating the "all subcategories" state
   */
  isParentOnlySelected(
    _subcategory: AnySubcategoryType,
    category: CampsiteCategory,
  ): boolean {
    return (
      this.categoryIds.includes(category.id) &&
      this.selectedSubcategoryFacetSlug === null
    )
  }

  /**
   * Handles subcategory checkbox toggle. Only one can be checked globally.
   */
  onToggleSubcategory(subcategory: AnySubcategoryType, checked: boolean) {
    // Prevent validation during subcategory updates to avoid race conditions
    this.isUpdatingSubcategory = true

    try {
      if (checked && subcategory.facetSlug) {
        this.selectedSubcategoryFacetSlug = subcategory.facetSlug

        // Use CategoryFacetLinker to add facet with its required category
        const result = this.categoryFacetLinker.addFacetWithCategory(
          this.categoryIds,
          this.facets.filter((f) => f !== subcategory.facetSlug), // Remove if already exists
          subcategory.facetSlug,
        )

        this.submitCategories(result.categoryIds)
      } else {
        // Deselecting a subcategory
        this.selectedSubcategoryFacetSlug = null

        if (subcategory.facetSlug) {
          // Use CategoryFacetLinker to remove facet and its category if needed
          const result = this.categoryFacetLinker.removeFacetWithCategory(
            this.categoryIds,
            this.facets,
            subcategory.facetSlug,
            true, // Remove parent category if no other facets remain
          )

          this.submitCategories(result.categoryIds)
        } else {
          this.submitCategories([])
        }
      }
      this.emitSubcategoryChange()
    } finally {
      // Re-enable validation after subcategory update is complete
      // Use nextTick to ensure prop updates have been processed
      void this.$nextTick(() => {
        this.isUpdatingSubcategory = false
      })
    }
  }

  /**
   * Emit the current selected subcategory facetSlug (or empty if none)
   */
  emitSubcategoryChange() {
    // Gather all subcategory facetSlugs
    const allSubcategorySlugs: string[] = []
    this.categoryGroups.forEach((group) => {
      group.categories.forEach((cat) => {
        if (cat.subcategories && cat.subcategories.length > 0) {
          cat.subcategories.forEach((sub) => {
            if (sub.facetSlug) allSubcategorySlugs.push(sub.facetSlug)
          })
        }
      })
    })
    const enabled = this.selectedSubcategoryFacetSlug
      ? [this.selectedSubcategoryFacetSlug]
      : []
    const disabled = this.selectedSubcategoryFacetSlug
      ? allSubcategorySlugs.filter(
          (slug) => slug !== this.selectedSubcategoryFacetSlug,
        )
      : allSubcategorySlugs

    // Validate user's facet selection before emitting
    this.validateUserSelection(this.categoryIds, enabled)
    this.$emit('subcategoryGroupChange', {
      enabled,
      disabled,
    })
  }

  /**
   * If facets prop is set, initialize the selected subcategory
   */
  initializeSelectedSubcategory() {
    let found: string | null = null
    for (const group of this.categoryGroups) {
      for (const category of group.categories) {
        if (category.subcategories && category.subcategories.length > 0) {
          for (const subcategory of category.subcategories) {
            if (
              subcategory.facetSlug &&
              this.facets.includes(subcategory.facetSlug)
            ) {
              found = subcategory.facetSlug
              break
            }
          }
        }
      }
    }
    this.selectedSubcategoryFacetSlug = found
  }

  getParentCategoryOfSubcategory(
    subcategoryFacetSlug: string,
  ): CampsiteCategory | null {
    return this.categoryFacetLinker.getParentCategoryForFacet(
      subcategoryFacetSlug,
    )
  }

  getCategoryById(categoryId: string): CampsiteCategory | null {
    for (const group of this.categoryGroups) {
      for (const category of group.categories) {
        if (category.id === categoryId) {
          return category
        }
      }
    }
    return null
  }
}

export default toNative(CategoriesSelector)
</script>

<style scoped>
fieldset ~ fieldset {
  margin: 1.5em 0;
}

fieldset {
  border-bottom: 1px solid var(--colour-table-borders);
  margin: 0;
}

legend {
  font-size: var(--body);
  font-weight: 600;
}

.category-checkbox {
  border-top: 1px solid var(--colour-table-borders);
  display: block;
  padding: 0.5em;
}

.category-checkbox :deep(label) {
  margin: 0;
}

.category-icon-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-right: 0.3em;
}

.category-icon-container .count {
  min-width: 0;
  margin: 0.3em auto auto auto;
  text-align: center;
}

.category-icon-container .count.count-hidden {
  display: none;
}

.category-icon {
  font-size: 1.5em;
  width: 2.75em;
  flex-shrink: 0;
}

.count {
  min-width: 30px;
  margin-left: auto;
  text-align: right;
}

.disabled .category-icon {
  filter: grayscale(1);
  opacity: 0.3;
}

.subcategories-wrapper {
  align-items: center;
  column-gap: 0.75em;
  display: flex;
  flex-wrap: wrap;
  row-gap: 0.1em;
}

.subcategory-checkbox {
  align-items: center;
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  position: relative;
  white-space: nowrap;
}

.subcategory-checkbox :deep(label) {
  align-items: center;
  color: var(--colour-text-default);
  cursor: pointer;
  display: flex;
  gap: 0.35em;
  margin: 0;
  padding: 0.25em 0.25em 0.25em 0.75em;
  position: relative;
  transition: all 0.2s ease;
}

.subcategory-checkbox :deep(label span) {
  line-height: 1;
}

.subcategory-count {
  min-width: 22px;
  padding-top: 0.2em;
}

.is-empty {
  opacity: 0.5;
}

.is-byo-main-category {
  border-top: 1px solid var(--colour-table-borders);
  padding: 0.4em 0;
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: start;
}

.is-byo-main-category .category-icon-container {
  margin-right: 0;
}

.is-byo-main-category .category-checkbox {
  border-top: none;
  display: inline-flex;
  padding: 0;
}

.is-byo-main-category .category-checkbox :deep(label) {
  pointer-events: none;
}

.is-byo-main-category .category-checkbox .category-icon {
  width: 2em;
}

.is-byo-main-category .subcategory-checkbox :deep(.checkbox-tick) {
  margin-right: 0.2em;
}

.count-hidden {
  visibility: hidden;
}

.desktop .subcategory-checkbox :deep(label) {
  padding: 0.5em 0.5em 0.5em 0.75em;
}

.faux-subcategory {
  margin-left: auto;
}

.parent-selected-state {
  opacity: 0.7;
}

.parent-selected-state :deep(.checkbox-tick) {
  border-color: var(--colour-text-default);
  opacity: 0.5;
}

.electric-facet-toggle.is-byo-main-category {
  padding: 0.6em 0 0.6em 0.3em;
}
.electric-facet-toggle img {
  margin-left: 0.6em;
  filter: grayscale(100%) brightness(150%);
  transition: 0.2s filter ease-in;
}
.electric-facet-toggle :deep(.slide-toggle) {
  flex-direction: row;
  gap: 0.25em;
}
.electric-facet-toggle
  :deep(.slide-toggle input:checked + .checkbox-slider + img) {
  filter: grayscale(0%) brightness(100%);
}
</style>
